/* Biometric history tab — extracted from BiometricPunch.js (iter85).
 * Receives all state/callbacks via a single spread props bag. Every
 * `data-testid` is preserved so existing e2e specs keep working. */
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Fingerprint, ClockCounterClockwise, Trash, UserCircle, ArrowClockwise, ArrowsClockwise, CurrencyDollar, WarningCircle, DownloadSimple, ArrowSquareOut } from '@phosphor-icons/react';

export const BiometricHistoryTab = ({
  campaignMonth, setCampaignMonth, campaignStats,
  loadCampaignStats, runReminderCampaign, reminderBusy,
  openComplianceReport, regenerateCompliance, complianceBusy,
  historyEmployee, setHistoryEmployee, teachers, staff,
  loadPayslipsHistory, historyLoading, payslipsHistory,
  downloadHistoryPdf, sendSignLink, copySignLink,
}) => (
  <>
          {/* iter58 — Campaign dashboard */}
          <Card className="border border-[#E2E8F0] shadow-none" data-testid="campaign-dashboard">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="font-outfit font-semibold">
                    Campagne de signature — {campaignMonth}
                  </h3>
                  <p className="text-xs text-[#64748B]">
                    Suivi mensuel des bulletins signés / en attente / relancés. Le cron J+2 et J+5
                    relance automatiquement chaque jour à 09:15 UTC.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input type="month" value={campaignMonth}
                    onChange={(e) => setCampaignMonth(e.target.value)}
                    className="h-9 rounded-md border border-[#E2E8F0] px-2 text-sm bg-white"
                    data-testid="campaign-month-input" />
                  <Button size="sm" variant="outline" onClick={loadCampaignStats}
                    data-testid="campaign-refresh-btn">
                    <ArrowClockwise size={14} className="mr-1" /> Rafraîchir
                  </Button>
                  <Button size="sm" onClick={runReminderCampaign} disabled={reminderBusy}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="campaign-run-btn">
                    ⚡ Lancer la campagne
                  </Button>
                  <Button size="sm" variant="outline" onClick={openComplianceReport}
                    className="border-purple-400 text-purple-700 hover:bg-purple-50"
                    data-testid="compliance-open-btn">
                    📊 Rapport de conformité
                  </Button>
                  <Button size="sm" variant="outline" onClick={regenerateCompliance}
                    disabled={complianceBusy}
                    className="border-purple-400 text-purple-700 hover:bg-purple-50"
                    data-testid="compliance-regen-btn">
                    ↻ Régénérer
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="rounded-lg border border-[#E2E8F0] bg-slate-50 p-3">
                  <p className="text-[10px] uppercase tracking-wide text-[#64748B] font-bold">Total</p>
                  <p className="text-2xl font-outfit font-bold" data-testid="stat-total">
                    {campaignStats?.total ?? '—'}
                  </p>
                  <p className="text-[10px] text-[#94A3B8]">bulletins émis</p>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-bold">Signés</p>
                  <p className="text-2xl font-outfit font-bold text-emerald-700" data-testid="stat-signed">
                    {campaignStats?.signed ?? '—'}
                  </p>
                  <p className="text-[10px] text-emerald-700/80">
                    {campaignStats?.signature_rate ?? 0}% de signatures
                  </p>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-[10px] uppercase tracking-wide text-amber-800 font-bold">En attente</p>
                  <p className="text-2xl font-outfit font-bold text-amber-800" data-testid="stat-pending">
                    {campaignStats?.pending ?? '—'}
                  </p>
                  <p className="text-[10px] text-amber-800/80">
                    {campaignStats?.oldest_pending_days
                      ? `plus ancien: ${campaignStats.oldest_pending_days}j`
                      : '—'}
                  </p>
                </div>
                <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
                  <p className="text-[10px] uppercase tracking-wide text-orange-800 font-bold">Relancés J+2</p>
                  <p className="text-2xl font-outfit font-bold text-orange-800" data-testid="stat-reminded-j2">
                    {campaignStats?.reminded_j2 ?? '—'}
                  </p>
                  <p className="text-[10px] text-orange-800/80">1er rappel</p>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                  <p className="text-[10px] uppercase tracking-wide text-red-800 font-bold">Relancés J+5</p>
                  <p className="text-2xl font-outfit font-bold text-red-800" data-testid="stat-reminded-j5">
                    {campaignStats?.reminded_j5 ?? '—'}
                  </p>
                  <p className="text-[10px] text-red-800/80">2e rappel (urgent)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="font-outfit font-semibold">Historique des bulletins de paie</h3>
                  <p className="text-xs text-[#64748B]">
                    Chaque bulletin généré est archivé. Re-téléchargez-le à tout moment sans recalcul,
                    et partagez le lien de signature avec l&apos;employé.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <select value={historyEmployee} onChange={(e) => setHistoryEmployee(e.target.value)}
                    className="h-9 rounded-md border border-[#E2E8F0] px-2 text-sm bg-white"
                    data-testid="history-employee-filter">
                    <option value="">Tous les employés</option>
                    {teachers.map((t) => (
                      <option key={`t-${t.id}`} value={t.id}>
                        {t.last_name} {t.first_name} (Enseignant)
                      </option>
                    ))}
                    {staff.map((s) => (
                      <option key={`s-${s.id}`} value={s.id}>
                        {s.last_name} {s.first_name} ({s.staff_type})
                      </option>
                    ))}
                  </select>
                  <Button variant="outline" size="sm" onClick={loadPayslipsHistory}
                    data-testid="history-refresh-btn">
                    <ArrowClockwise size={14} className="mr-1" /> Rafraîchir
                  </Button>
                </div>
              </div>

              {historyLoading ? (
                <p className="text-sm text-[#94A3B8] text-center py-8">Chargement…</p>
              ) : payslipsHistory.length === 0 ? (
                <p className="text-sm text-[#94A3B8] italic text-center py-8">
                  Aucun bulletin archivé. Générez un bulletin dans l&apos;onglet &laquo;Rapport paie&raquo; pour créer un historique.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employé</TableHead>
                      <TableHead>Catégorie</TableHead>
                      <TableHead>Période</TableHead>
                      <TableHead className="text-right">Net</TableHead>
                      <TableHead className="text-center">Signature</TableHead>
                      <TableHead className="text-right">Généré le</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payslipsHistory.map((p) => (
                      <TableRow key={p.id} data-testid={`history-row-${p.id}`}>
                        <TableCell className="font-medium">{p.employee_name}</TableCell>
                        <TableCell>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                            {p.staff_type}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {p.period_start} → {p.period_end}
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold text-emerald-700">
                          {(p.total_pay || 0).toLocaleString('fr-FR')} F
                        </TableCell>
                        <TableCell className="text-center">
                          {p.signed ? (
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-emerald-100 text-emerald-700"
                              title={p.signed_at || ''}>
                              ✓ SIGNÉ
                            </span>
                          ) : p.last_reminder_offset ? (
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-orange-100 text-orange-800"
                              title={`Relancé le ${p.last_reminder_at || ''}`}>
                              🔔 RELANCÉ J+{p.last_reminder_offset}
                            </span>
                          ) : (
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-amber-100 text-amber-800">
                              EN ATTENTE
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-[10px] text-[#64748B]">
                          {p.generated_at ? new Date(p.generated_at).toLocaleDateString('fr-FR') : '—'}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex gap-1 justify-center">
                            <Button size="sm" variant="outline"
                              onClick={() => downloadHistoryPdf(p.id, p.employee_name)}
                              className="border-[#0052FF] text-[#0052FF] hover:bg-blue-50 h-7 px-2"
                              data-testid={`history-download-${p.id}`}>
                              📄 PDF
                            </Button>
                            {!p.signed && (
                              <>
                                <Button size="sm" variant="outline"
                                  onClick={() => sendSignLink(p.id)}
                                  className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 h-7 px-2"
                                  data-testid={`history-send-${p.id}`}>
                                  📧 Envoyer
                                </Button>
                                <Button size="sm" variant="outline"
                                  onClick={() => copySignLink(p.id)}
                                  className="border-amber-400 text-amber-700 hover:bg-amber-50 h-7 px-2"
                                  data-testid={`history-signlink-${p.id}`}>
                                  🔗 Lien
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
  </>
);
